import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  TextField,
  Chip,
  Checkbox,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import { PersonAdd } from '@mui/icons-material';
import { ReportDraft, SparePlayer, PlayerAppearance } from '../../types/matchReport.ts';
import { MatchReportAPI } from '../../utils/matchReportApi.ts';
import PlayerSearchAutocomplete from './PlayerSearchAutocomplete.tsx';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface AppearancesStepProps {
  draft: ReportDraft;
  onChange: (_updates: Partial<ReportDraft>) => void;
  teamId: number;
  gameId?: number;
}

interface ShirtNumberCellProps {
  userTenantId: number;
  initialValue: string;
  onCommit: (_userTenantId: number, _value: string) => void;
}

// Local-state input so typing doesn't trigger parent re-renders / row re-sorts
// until the user finishes editing (blur or Enter). Prevents losing focus
// after typing a single digit.
const ShirtNumberCell: React.FC<ShirtNumberCellProps> = ({ userTenantId, initialValue, onCommit }) => {
  const [value, setValue] = useState<string>(initialValue);

  // Sync when the underlying value changes from outside (e.g., row data reload).
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const commit = () => {
    if (value !== initialValue) {
      onCommit(userTenantId, value);
    }
  };

  return (
    <TextField
      size="small"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          (e.target as HTMLInputElement).blur();
        }
      }}
      inputProps={{
        min: 1,
        max: 99,
        style: { textAlign: 'center' }
      }}
      sx={{ width: 70 }}
    />
  );
};

export const AppearancesStep: React.FC<AppearancesStepProps> = ({
  draft,
  onChange,
  teamId,
  gameId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rosterPlayers, setRosterPlayers] = useState<PlayerAppearance[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Load team roster on mount
  useEffect(() => {
    const loadRoster = async () => {
      try {
        setLoading(true);
        const roster = await MatchReportAPI.fetchTeamRoster(teamId);

        // Initialize appearances for roster players if not already set
        if (draft.appearances.length === 0) {
          // Default selection follows the player RSVPs for this game. If the RSVPs can't
          // be loaded we fall back to selecting everyone who isn't injured.
          let rsvpMap: Map<number, boolean> | null = null;
          if (gameId) {
            try {
              rsvpMap = await MatchReportAPI.fetchGameRsvpMap(gameId);
            } catch {
              rsvpMap = null;
            }
          }

          const initialAppearances: PlayerAppearance[] = roster.map(player => ({
            userTenantId: player.userTenantId,
            playerName: `${player.firstName} ${player.lastName}`,
            firstName: player.firstName,
            lastName: player.lastName,
            email: player.email,
            // Injured players are never selected, even if they RSVP'd yes before being injured
            played: player.playerStatus !== 'Injured'
              && (rsvpMap === null || rsvpMap.get(Number(player.userTenantId)) === true),
            isSpare: player.membershipType === 'LongTermSpare', // Long-term spares treated as spare appearances
            shirtNumber: player.shirtNumber?.toString() || '',
            position: player.position,
            playerStatus: player.playerStatus,
            membershipType: player.membershipType,
          }));
          onChange({ appearances: initialAppearances });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load team roster');
      } finally {
        setLoading(false);
      }
    };

    if (teamId) {
      loadRoster();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, gameId]); // Intentionally exclude draft.appearances to prevent infinite loop

  // Helper function to sort players like a game sheet
  const sortPlayersForGameSheet = (players: PlayerAppearance[]) => {
    return [...players].sort((a, b) => {
      // Injured players go to the bottom
      const aIsInjured = a.playerStatus === 'Injured';
      const bIsInjured = b.playerStatus === 'Injured';
      
      if (aIsInjured && !bIsInjured) return 1;
      if (!aIsInjured && bIsInjured) return -1;
      
      // Then by jersey number (numeric sort)
      const aNum = a.shirtNumber ? parseInt(a.shirtNumber, 10) : Number.MAX_SAFE_INTEGER;
      const bNum = b.shirtNumber ? parseInt(b.shirtNumber, 10) : Number.MAX_SAFE_INTEGER;
      
      if (aNum !== bNum) {
        return aNum - bNum;
      }
      
      // Finally by name alphabetically (last name, then first name)
      const aLastName = (a.lastName || '').toUpperCase();
      const bLastName = (b.lastName || '').toUpperCase();
      
      if (aLastName !== bLastName) {
        return aLastName.localeCompare(bLastName);
      }
      
      const aFirstName = (a.firstName || '').toUpperCase();
      const bFirstName = (b.firstName || '').toUpperCase();
      
      return aFirstName.localeCompare(bFirstName);
    });
  };

  // Sync rosterPlayers state when appearances change
  useEffect(() => {
    const rosterPlayersFromAppearances = (draft.appearances || [])
      .filter(p => !p.isSpare && p.userTenantId)
      .map(p => ({
        ...p,
        userTenantId: Number(p.userTenantId), // Ensure numeric ID
      }));
    
    // Sort players in game sheet order
    const sortedPlayers = sortPlayersForGameSheet(rosterPlayersFromAppearances);
    setRosterPlayers(sortedPlayers);
  }, [draft.appearances]);

  // Ref to store AG Grid API
  const gridRef = React.useRef<any>(null);

  // Handle grid ready - set initial selection
  const handleGridReady = useCallback((params: any) => {
    gridRef.current = params;
    
    // Set initial selection based on played status
    const playedUserTenantIds = (draft.appearances || [])
      .filter(app => !app.isSpare && app.played)
      .map(app => app.userTenantId);
    
    params.api.forEachNode((node: any) => {
      const isSelected = playedUserTenantIds.includes(node.data.userTenantId);
      node.setSelected(isSelected);
    });
  }, [draft.appearances]);

  // Sync AG Grid selection with played status
  useEffect(() => {
    if (gridRef.current?.api && rosterPlayers.length > 0) {
      const playedUserTenantIds = (draft.appearances || [])
        .filter(app => !app.isSpare && app.played)
        .map(app => app.userTenantId);
      
      gridRef.current.api.forEachNode((node: any) => {
        const isSelected = playedUserTenantIds.includes(node.data.userTenantId);
        node.setSelected(isSelected);
      });
    }
  }, [draft.appearances, rosterPlayers]);

  const handleShirtNumberChange = useCallback((userTenantId: number, shirtNumber: string) => {
    const updatedAppearances = (draft.appearances || []).map(app =>
      app.userTenantId === userTenantId ? { ...app, shirtNumber } : app
    );
    onChange({ appearances: updatedAppearances });
    
    // Update local roster players state
    setRosterPlayers(prev => (prev || []).map(app =>
      app.userTenantId === userTenantId ? { ...app, shirtNumber } : app
    ));
  }, [draft.appearances, onChange]);

  const handleAddSpare = (spare: SparePlayer) => {
    const spareName = spare.name || `${spare.firstName} ${spare.lastName}`;
    const newAppearance: PlayerAppearance = {
      userTenantId: spare.userTenantId,
      playerName: spareName,
      firstName: spare.firstName,
      lastName: spare.lastName,
      email: spare.email,
      played: true,
      isSpare: true,
      shirtNumber: '',
    };

    onChange({
      appearances: [...draft.appearances, newAppearance]
    });
  };

  const handleRemoveSpare = (userTenantId: number) => {
    const updatedAppearances = draft.appearances.filter(
      app => app.userTenantId !== userTenantId
    );
    onChange({ appearances: updatedAppearances });
  };

  


  // Handle AG Grid selection changes
  const handleSelectionChanged = useCallback((event: any) => {
    const selectedNodes = event.api.getSelectedNodes();
    const selectedUserTenantIds = new Set(selectedNodes.map((node: any) => node.data.userTenantId));
    
    const updatedAppearances = (draft.appearances || []).map(app => {
      if (!app.isSpare) {
        return { ...app, played: selectedUserTenantIds.has(app.userTenantId) };
      }
      return app;
    });
    
    onChange({ appearances: updatedAppearances });
  }, [draft.appearances, onChange]);

  // Custom cell renderer for shirt number input.
  // Uses local state and only commits on blur/Enter so that typing a digit
  // doesn't trigger a re-sort that would steal focus from the field.
  const ShirtNumberRenderer = useCallback((props: any) => {
    const { data } = props;
    return (
      <ShirtNumberCell
        userTenantId={data.userTenantId}
        initialValue={data.shirtNumber || ''}
        onCommit={handleShirtNumberChange}
      />
    );
  }, [handleShirtNumberChange]);

  // Custom cell renderer for player name with status indicators
  const PlayerNameRenderer = useCallback((props: any) => {
    const { data } = props;
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2">
          {data.playerName}
        </Typography>
        {data.playerStatus === 'Injured' && (
          <Chip 
            label="Injured" 
            size="small" 
            color="error"
            sx={{ height: 20, fontSize: '0.7rem' }}
            onClick={() => {}}
          />
        )}
        {data.membershipType === 'LongTermSpare' && (
          <Chip 
            label="Spare" 
            size="small" 
            color="default"
            sx={{ height: 20, fontSize: '0.7rem' }}
            onClick={() => {}}
          />
        )}
      </Box>
    );
  }, []);

  // AG Grid column definitions
  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: 'Player Name',
      field: 'playerName',
      flex: 1,
      minWidth: 200,
      sortable: true,
      cellRenderer: PlayerNameRenderer,
    },
    {
      headerName: 'Shirt #',
      field: 'shirtNumber',
      width: 100,
      cellRenderer: ShirtNumberRenderer,
      sortable: false,
      filter: false,
    },
  ], [ShirtNumberRenderer, PlayerNameRenderer]);

  const playedCount = draft.appearances.filter(app => app.played).length;
  const spareCount = draft.appearances.filter(app => app.played && app.isSpare).length;
  const rosterPlayedCount = draft.appearances.filter(app => app.played && !app.isSpare).length;

  const getShirtNumberWarnings = () => {
    const playedAppearances = draft.appearances.filter(app => app.played);
    const shirtNumbers = playedAppearances
      .map(app => app.shirtNumber)
      .filter(num => num && num.trim() !== '');
    
    const duplicates = shirtNumbers.filter((num, index) => 
      shirtNumbers.indexOf(num) !== index
    );
    
    return [...new Set(duplicates)];
  };

  const duplicateShirtNumbers = getShirtNumberWarnings();

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Loading team roster...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom align="center">
        Player Appearances
      </Typography>
      
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        Select which players participated in this match and assign shirt numbers.
      </Typography>

      {/* Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
            <Box>
              <Typography variant="h6" color="primary">
                {playedCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Players
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" color="secondary">
                {rosterPlayedCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Roster Players
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" color="warning.main">
                {spareCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Spare Players
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Warnings */}
      {duplicateShirtNumbers.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Duplicate shirt numbers detected: {duplicateShirtNumbers.join(', ')}
        </Alert>
      )}

      {playedCount > 16 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have {playedCount} players selected. This is more than the typical 11 starters + 5 substitutes.
        </Alert>
      )}

      {/* Team Roster DataGrid */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Team Roster
            </Typography>
            
          </Box>
          
          <Box sx={{ width: '100%' }}>
            {rosterPlayers.length > 0 ? (
              isMobile ? (
                /* Mobile: checkbox list */
                <Box data-testid="mobile-roster-list">
                  {rosterPlayers.map((player, index) => (
                    <React.Fragment key={player.userTenantId}>
                      {index > 0 && <Divider />}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 0.5 }}>
                        <Checkbox
                          checked={draft.appearances.find(a => a.userTenantId === player.userTenantId)?.played ?? false}
                          onChange={(e) => {
                            const updatedAppearances = draft.appearances.map(app =>
                              app.userTenantId === player.userTenantId ? { ...app, played: e.target.checked } : app
                            );
                            onChange({ appearances: updatedAppearances });
                          }}
                          inputProps={{ 'aria-label': `Select ${player.playerName}` }}
                          sx={{ p: 0.5 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {player.playerName}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                            {player.playerStatus === 'Injured' && (
                              <Chip
                                label="Injured"
                                size="small"
                                color="error"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                                clickable={false}
                                onClick={() => {}}
                              />
                            )}
                            {player.membershipType === 'LongTermSpare' && (
                              <Chip
                                label="Spare"
                                size="small"
                                color="default"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                                clickable={false}
                                onClick={() => {}}
                              />
                            )}
                          </Box>
                        </Box>
                        <TextField
                          size="small"
                          value={player.shirtNumber || ''}
                          onChange={(e) => handleShirtNumberChange(player.userTenantId, e.target.value)}
                          placeholder="#"
                          inputProps={{
                            min: 1,
                            max: 99,
                            style: { textAlign: 'center', padding: '6px 4px' }
                          }}
                          sx={{ width: 52 }}
                        />
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              ) : (
                /* Desktop: AG Grid */
                <div
                  data-testid="desktop-roster-grid"
                  className="ag-theme-material"
                  style={{ width: '100%' }}
                >
                  <AgGridReact
                    ref={gridRef}
                    rowData={rosterPlayers}
                    columnDefs={columnDefs}
                    getRowId={(params) => params.data.userTenantId.toString()}
                    theme="legacy"
                    domLayout="autoHeight"
                    headerHeight={48}
                    rowHeight={56}
                    rowSelection={{ 
                      mode: 'multiRow', 
                      checkboxes: true,
                      headerCheckbox: true,
                      enableClickSelection: false 
                    }}
                    onGridReady={handleGridReady}
                    onSelectionChanged={handleSelectionChanged}
                    suppressCellFocus={true}
                    animateRows={true}
                    suppressRowHoverHighlight={false}
                  />
                </div>
              )
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                {loading ? 'Loading team roster...' : 'No players found for this team.'}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Spare Players */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAdd />
            Spare Players
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <PlayerSearchAutocomplete
              onPlayerSelected={handleAddSpare}
              label="Search and add spare players"
              placeholder="Type player name..."
            />
          </Box>

          {/* Display added spares */}
          {draft.appearances.filter(app => app.isSpare).length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {draft.appearances.filter(app => app.isSpare).map(spare => (
                <Chip
                  key={spare.userTenantId}
                  label={spare.playerName}
                  onDelete={() => handleRemoveSpare(spare.userTenantId)}
                  color={spare.played ? 'primary' : 'default'}
                  variant={spare.played ? 'filled' : 'outlined'}
                  clickable={false}
                  onClick={() => {}}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Validation Messages */}
      <Box sx={{ mt: 2 }}>
        {playedCount === 0 && (
          <Typography variant="body2" color="warning.main" align="center">
            Please select at least one player who participated in the match
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AppearancesStep;